import type { UserAuthPaths, UseQueryOptions } from '../../types'
import { useOpenapi, useCookie, useQuery, useCoreConfig, useUserInfo, useMutation, navigateTo, OpenapiError } from '#imports'

/**
 * @title useUserAuthClient
 * @see Docs https://user-auth-prod.spilnu.dk/api/documentation
 */
export const useUserAuthClient = () => {
    const { site } = useCoreConfig()
    const client = useOpenapi<UserAuthPaths>({
        baseUrl: '/gateway/user-auth/api',
        service: 'userAuth'
    })

    const session_expiry = useCookie('session_expiry', {
        readonly: true
    })

    const getLoginLimitsCheck = (options: Pick<UseQueryOptions<any>, 'enabled' | 'lazy' | 'server'> = {}) => {
        const { enabled = !!session_expiry.value, lazy = true, server = false } = options

        const query = useQuery(async () => {
            const response = await client.GET('/v1/public/login-limits-check')

            return response
        }, {
            key: 'userAuth:loginLimitsCheck',
            server,
            lazy,
            enabled
        })

        return query
    }

    const getSessionInfo = (options: Pick<UseQueryOptions<any>, 'enabled' | 'lazy' | 'server'> = {}) => {
        const { enabled = !!session_expiry.value, lazy = true, server = false } = options

        const query = useQuery(async () => {
            const { data } = await client.GET('/v1/public/session-info', {
                params: {
                    query: {
                        brand: site.brand
                    }
                }
            })

            return data
        }, {
            key: 'userAuth:sessionInfo',
            server,
            lazy,
            enabled
        })

        return query
    }

    function useLogout(redirectUrl?: string) {
        const { client } = useUserAuthClient()

        const { userJustLoggedIn } = useUserInfo()

        function redirectOnLogout() {
            // User is already logged out, reload window
            navigateTo(redirectUrl || '/', { external: true })
        }

        const mutation = useMutation(async () => {
            const response = await client.POST('/v1/public/auth/logout')

            return response
        }, {
            onSuccess() {
            // Unset userJustLoggedIn cookie
                userJustLoggedIn.value = undefined

                redirectOnLogout()
            },
            onError(err) {
                if (OpenapiError.isError(err)) {
                    if (err.status === 400) {
                        redirectOnLogout()
                    }
                }
            },
        })

        return mutation
    }

    return {
        client,
        getLoginLimitsCheck,
        getSessionInfo,
        useLogout
    }
}
